/**
 * ProductInfo class
 * Subscribes to cart updates and variant changes.
 * Handles quantity rule boundary corrections.
 */
if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      constructor() {
        super();

        // Record recently viewed product
        if (window.localStorage) this.addRecentlyViewedProduct();

        this.quantityForm = document.getElementById(
          `Quantity-Selector-${this.dataset.section}`,
        ); // Quantity selector container
        if (this.quantityForm)
          this.quantityInput =
            this.quantityForm.querySelector("quantity-input"); // Product quantity input
        if (!this.quantityInput) return;

        this.miniQuantityForm = document.getElementById(
          `Mini-Quantity-Selector-${this.dataset.section}`,
        ); // Mini quantity selector container
        if (this.miniQuantityForm)
          this.miniQuantityInput =
            this.miniQuantityForm.querySelector("quantity-input"); // Mini product quantity input

        // Initialize quantity boundary corrections
        // Defer to next frame to ensure child elements are fully mounted
        requestAnimationFrame(() => {
          this.quantityInput.setQuantityBoundaries();
          if (this.miniQuantityInput)
            this.miniQuantityInput.setQuantityBoundaries();
        });

        // Subscribe to cart updates -> fetch latest quantity rules and correct boundaries
        this.cartUpdateUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (event.productVariantId !== this.currentVariantId) return;
            this.fetchQuantityRules();
          },
        );

        // Subscribe to variant changes
        this.variantChangeUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (
              event.data.sectionId !== this.dataset.section ||
              event.data.html == null
            )
              return;

            this.applyQuantityRulesFromHtml(event.data.html);
          },
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      get currentVariantId() {
        return this.querySelector(
          ".product-info-price .product-form input[name='id']",
        )?.value;
      }

      /**
       * Re-fetch the latest quantity rules from the server
       * and correct selector boundaries.
       */
      fetchQuantityRules() {
        const variantId = this.currentVariantId;
        if (!variantId) return;

        webvista
          .fetchHtml(
            `${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`,
          )
          .then((html) => {
            this.applyQuantityRulesFromHtml(html);
          })
          .catch((e) => {
            webvista.popToast(
              window["accessibilityStrings"]["unknownError"],
              "error",
            );
          });
      }

      /**
       * Apply quantity rules from a parsed HTML document to both
       * main and mini quantity selectors.
       * @param {Document} html - Parsed HTML containing updated quantity data
       */
      applyQuantityRulesFromHtml(html) {
        const sourceQuantityForm = html.getElementById(
          `Quantity-Selector-${this.dataset.section}`,
        );
        this.updateQuantityRules(this.quantityForm, sourceQuantityForm);
        this.quantityInput.setQuantityBoundaries();

        // Update mini checkout quantity rules
        if (this.miniQuantityForm) {
          const miniSourceQuantityForm = html.getElementById(
            `Mini-Quantity-Selector-${this.dataset.section}`,
          );
          this.updateQuantityRules(
            this.miniQuantityForm,
            miniSourceQuantityForm,
          );
          this.miniQuantityInput.setQuantityBoundaries();
        }
      }

      /**
       * Update quantity rules for a selector.
       * .quantity-input: update data-cart-quantity, data-min, data-max, step
       * .quantity-label: update the cart quantity label for the current variant
       * @param target - Current selector element
       * @param source - Source selector element with updated data
       */
      updateQuantityRules(target, source) {
        if (!target || !source) return;

        // Update .quantity-input attributes
        const targetInput = target.querySelector("input[name='quantity']");
        const updatedInput = source.querySelector("input[name='quantity']");

        if (targetInput && updatedInput) {
          const attributes = [
            "data-cart-quantity",
            "data-min",
            "data-max",
            "min",
            "max",
            "step",
          ];
          attributes.forEach((attribute) => {
            const value = updatedInput.getAttribute(attribute);
            if (value != null) {
              targetInput.setAttribute(attribute, value);
            } else {
              targetInput.removeAttribute(attribute);
            }
          });
        }

        // Update .quantity-label
        const targetLabel = target.querySelector(".quantity-label");
        const updatedLabel = source.querySelector(".quantity-label");

        if (targetLabel && updatedLabel) {
          targetLabel.innerHTML = updatedLabel.innerHTML;
        }
      }

      /**
       * Add product to recently viewed history.
       */
      addRecentlyViewedProduct() {
        if (!this.dataset.id) return;

        const recentlyViewedData =
          webvista.retrieveData(RECENTLY_VIEWED_KEY) || [];
        const index = recentlyViewedData.indexOf(this.dataset.id);
        if (index !== -1) {
          recentlyViewedData.splice(index, 1); // Remove existing entry
        }
        recentlyViewedData.push(this.dataset.id); // Add new entry

        // Trim oldest entries, keeping at most 10 items
        if (recentlyViewedData.length > 10) {
          recentlyViewedData.splice(0, recentlyViewedData.length - 10);
        }

        webvista.storeData(RECENTLY_VIEWED_KEY, recentlyViewedData);
      }
    },
  );
}
